import {liteClient as algoliasearch} from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import hits from 'instantsearch.js/es/widgets/hits/hits.js';
import index from 'instantsearch.js/es/widgets/index/index.js';
import infiniteHits from 'instantsearch.js/es/widgets/infinite-hits/infinite-hits.js';
import configure from 'instantsearch.js/es/widgets/configure/configure.js';
import refinementList from 'instantsearch.js/es/widgets/refinement-list/refinement-list.js';
import {history} from 'instantsearch.js/es/lib/routers/index.js';
import {emptyResults} from './emptyResults.js';
import {searchItemTemplate} from './templates/searchItemTemplate.js';
import {suggestedSearchTemplate} from './templates/suggestedSearchTemplate.js';
import {stateMapping} from './stateMapping.js';

/**
 * @param {object} searchParams
 * @param {Array} searchParams.searchBoxes
 * @param {string|HTMLElement} searchParams.hitsContainer
 * @param {string|HTMLElement} searchParams.suggestionsContainer
 * @param {boolean} searchParams.isSearchPage
 * @returns {object}
 */
export function createSearchInstance({searchBoxes = [], hitsContainer, suggestionsContainer, isSearchPage = false}) {
    const searchClient = algoliasearch(
        import.meta.env.VITE_ALGOLIA_APP_ID,
        import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY
    );

    const search = instantsearch({
        indexName: 'tag',
        searchClient,
        routing: isSearchPage && {
            router: history(),
            stateMapping,
        },
    });

    if (typeof suggestionsContainer === 'string') {
        suggestionsContainer = document.querySelector(suggestionsContainer);
    }

    if (typeof hitsContainer === 'string') {
        hitsContainer = document.querySelector(hitsContainer);
    }

    search.addWidgets([
        configure({
            hitsPerPage: 5,
            clickAnalytics: true,
            userToken: window.currentVisitor.id,
        }),

        ...searchBoxes,

        index({indexName: 'learning_resource'}).addWidgets([
            configure({
                hitsPerPage: 8,
                clickAnalytics: true,
                userToken: window.currentVisitor.id,
            }),

            infiniteHits({
                container: hitsContainer,
                templates: {
                    item: searchItemTemplate,
                    empty: emptyResults('resources'),
                    showMoreText(_, {html}) {
                        return html`Show More...`;
                    },
                },
            }),

            refinementList({
                container: document.querySelector('.searchToolbar__filterList'),
                attribute: 'model_type',
                sortBy: ['name:asc'],
                transformItems() {
                    return [
                        {
                            label: 'Article',
                            value: 'article',
                        },
                        {
                            label: 'Course',
                            value: 'course',
                        },
                        {
                            label: 'FAQ',
                            value: 'faq_section',
                        },
                        {
                            label: 'Lesson',
                            value: 'lesson',
                        },
                        {
                            label: 'Local Group',
                            value: 'local_group',
                        },
                        {
                            label: 'Master Class',
                            value: 'masterclass',
                        },
                        {
                            label: 'Topic',
                            value: 'tag',
                        },
                    ];
                },
                templates: {
                    item({label, value, isRefined}, {html}) {
                        return html`
                            <label
                                data-insights-filter="model_type:${label}"
                                class="ais-RefinementList-label ${isRefined
                                    ? 'ais-RefinementList-label--selected'
                                    : ''}"
                            >
                                <input
                                    type="checkbox"
                                    class="ais-RefinementList-checkbox"
                                    value="${value}"
                                    checked="${isRefined ? 'checked' : ''}"
                                />
                                <span class="ais-RefinementList-labelText">${label}</span>
                            </label>
                        `;
                    },
                },
            }),
        ]),

        hits({
            container: suggestionsContainer,
            templates: {
                item: suggestedSearchTemplate,
                empty: (_, {html}) => {
                    return html`No suggested searches`;
                },
            },
            transformItems(items, {results}) {
                return items.filter((item) => item.name?.toLowerCase() !== results.query.toLowerCase());
            },
        }),
    ]);

    search.start();

    /**
     * @param {object} indexUiState
     */
    function setInstantSearchUiState(indexUiState) {
        search.setUiState((uiState) => ({
            ...uiState,
            ['learning_resource']: {
                ...uiState['learning_resource'],
                // Reset the page when the search state changes.
                page: 1,
            },
            ['tag']: {
                ...uiState['tag'],
                ...indexUiState,
            },
        }));
    }

    suggestionsContainer?.addEventListener(
        'click',
        function (clickEvent) {
            const suggestionLink = clickEvent.target.closest('[data-instantsearch-query');

            if (!suggestionLink) {
                return;
            }

            clickEvent.preventDefault();
            setInstantSearchUiState({
                query: suggestionLink.dataset.instantsearchQuery,
            });
        },
        false
    );

    return {
        clearQuery: () => {
            setInstantSearchUiState({
                query: '',
            });
        },
    };
}
